import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Field, Input, Select } from '../../components/clients/form-primitives';
import { PillButton } from '../../components/clients/PillButton';
import { startBootstrap } from '../../lib/bootstrap-api';
import { extractErrorMessage } from '../../lib/errors';
import { useSaasProducts } from '../../hooks/use-saas-products';

type HistoryWindow = '12m' | '24m' | 'all';

interface FormState {
  api_url: string;
  api_key: string;
  history_window: HistoryWindow;
  is_dry_run: boolean;
}

const initial: FormState = {
  api_url: '',
  api_key: '',
  history_window: '12m',
  is_dry_run: false,
};

/**
 * Story 8.7 — Wizard 4 passos pra conectar SaaS:
 *  1. URL + API key + (HMAC secret — fora MVP, vai por env)
 *  2. Janela histórico
 *  3. Dry-run opcional
 *  4. Confirmação
 */
export function ConnectSaasPage(): JSX.Element {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const saas = useSaasProducts({ limit: 100 });
  const product = saas.data?.items.find((s) => s.slug === slug);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(initial);
  const [pending, setPending] = useState(false);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleConfirm() {
    if (!product) return;
    setPending(true);
    try {
      const result = await startBootstrap({
        saas_product_id: product.id,
        api_url: form.api_url,
        api_key: form.api_key,
        history_window: form.history_window,
        is_dry_run: form.is_dry_run,
      });
      toast.success(`Bootstrap iniciado (${form.is_dry_run ? 'dry-run' : 'real'}).`);
      navigate(`/integrations/runs/${result.runId}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao iniciar bootstrap.'));
      setPending(false);
    }
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-6 text-sm text-muted">
        SaaS slug <code>{slug}</code> não encontrado.
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-5 px-6 py-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Conectar {product.name}</h1>
        <p className="mt-1 text-sm text-muted">Passo {step} de 4</p>
      </header>

      <section className="flex flex-col gap-4 rounded-[12px] border border-line bg-panel p-5">
        {step === 1 && (
          <>
            <Field label="API URL" required hint="Ex: https://api.reservei.app/internal/v1">
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="url"
                  value={form.api_url}
                  onChange={(e) => set('api_url', e.target.value)}
                />
              )}
            </Field>
            <Field label="API Key" required>
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="password"
                  value={form.api_key}
                  onChange={(e) => set('api_key', e.target.value)}
                />
              )}
            </Field>
            <p className="text-[11px] text-muted-2">
              HMAC secret de webhooks outbound é configurado via env (encrypted ADR-007).
            </p>
          </>
        )}

        {step === 2 && (
          <Field label="Janela de histórico" required>
            {({ inputId }) => (
              <Select
                id={inputId}
                value={form.history_window}
                onChange={(e) => set('history_window', e.target.value as HistoryWindow)}
              >
                <option value="12m">Últimos 12 meses</option>
                <option value="24m">Últimos 24 meses</option>
                <option value="all">Tudo</option>
              </Select>
            )}
          </Field>
        )}

        {step === 3 && (
          <label className="flex items-center gap-2 text-[13px] text-ink">
            <input
              type="checkbox"
              checked={form.is_dry_run}
              onChange={(e) => set('is_dry_run', e.target.checked)}
              className="h-4 w-4 rounded border-line bg-panel"
            />
            Rodar em modo dry-run (não persiste — só report)
          </label>
        )}

        {step === 4 && (
          <div className="rounded-[10px] border border-line bg-panel-2 p-4 text-[13px]">
            <div className="font-semibold text-ink">Confirme:</div>
            <ul className="mt-2 space-y-1 text-muted">
              <li>SaaS: {product.name}</li>
              <li>
                API URL: <code>{form.api_url}</code>
              </li>
              <li>Histórico: {form.history_window}</li>
              <li>Modo: {form.is_dry_run ? 'dry-run' : 'real (persiste)'}</li>
            </ul>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <PillButton
            type="button"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1 || pending}
          >
            Voltar
          </PillButton>
          {step < 4 ? (
            <PillButton
              type="button"
              solid
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 1 && (!form.api_url || !form.api_key)}
            >
              Avançar
            </PillButton>
          ) : (
            <PillButton type="button" solid onClick={() => void handleConfirm()} disabled={pending}>
              {pending ? 'Iniciando…' : 'Iniciar bootstrap'}
            </PillButton>
          )}
        </div>
      </section>
    </div>
  );
}
